/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {ActivatedRoute, Router} from '@angular/router';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG, SEC_LEVEL_LIST} from '../../../../common/constants';
import {AutocompleteFilterComponent} from '../../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {LoadingCounter} from '../../../../core/helpers/loading-counter';
import {StateService} from '../../../../core/state/state.service';
import {FcorgRegionMapModel} from '../../../../models/fcorg-region-map.model';
import {Fcorg} from '../../../../models/fcorg.model';
import {GfsUserDtoModel} from '../../../../models/gfs-user-dto.model';
import {SecurityLevelModel} from '../../../../models/security-level.model';
import {User} from '../../../../models/user.model';
import {FcorgService} from '../../../../services/fcorg.service';
import {GfsUserService} from '../../../../services/gfs-user.service';
import {RegionService} from '../../../../services/region.service';

@Component({
    selector: 'app-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss'],
    standalone: false
})
export class EditUserComponent implements OnInit {
  @ViewChild(MatPaginator, {static: false}) paginator: MatPaginator;
  @ViewChild(MatSort, {static: false}) sort: MatSort;

  userForm: FormGroup;
  displayedColumns: string[] = ['fcorg', 'regions'];
  readonly loading = new LoadingCounter();

  user: User;
  editUser: User;
  selectedUserId: string;

  dataSource: MatTableDataSource<FcorgRegionMapModel> = new MatTableDataSource<FcorgRegionMapModel>([]);
  newGfsUser: GfsUserDtoModel;
  regionMap: FcorgRegionMapModel[] = [];

  fcorgRegionMapColumns: ColDef[] = [
    {field: 'fcorg', headerName: 'Global FCORG', filter: AutocompleteFilterComponent},
    {field: 'region', headerName: 'Region'}
  ];
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  selectedFcorgs: string[];
  fcorgList: Fcorg[];
  regionList: string[];
  regionsToSubmit: string[] = [];
  fcorgsToSubmit: string[] = [];

  selectedRole: SecurityLevelModel = {value: '', viewValue: ''};
  secList = SEC_LEVEL_LIST;

  constructor(private readonly userService: StateService,
              readonly snackbar: FabMessageService,
              readonly router: Router,
              private readonly route: ActivatedRoute,
              private readonly gfsUserService: GfsUserService,
              private readonly fcorgService: FcorgService,
              private readonly regionService: RegionService) {}

  get showSpinner(): boolean {
    return this.loading.active;
  }

  ngOnInit(): void {
    this.user = this.userService.user;
    this.selectedUserId = this.route.snapshot.queryParamMap.get('userId');
    this.loadUser(this.selectedUserId);
    this.loadFcorgs();
    this.loadRegions();
    setTimeout(() => {
      this.createForm();
    }, 1000);
    setTimeout(() => {
      this.loadUserMap();
    }, 1500);
  }

  /**
   * Creates form
   */
  createForm(): void {
    this.userForm = new FormGroup({
      id: new FormControl({value: this.selectedUserId, disabled: true}),
      firstName: new FormControl({value: this.editUser.firstName, disabled: true}, Validators.required),
      lastName: new FormControl({value: this.editUser.lastName, disabled: true}, Validators.required),
      role: new FormControl(this.selectedRole.value, Validators.required),
      fcorgs: new FormControl('', Validators.required),
      regions: new FormControl('', Validators.required)
    });
  }

  /**
   * Creates a userDTO object from user input and call service to update user
   */
  onSubmit(): void {
    // loop thorugh regionMap and push final regions/fcorgs
    this.regionMap.forEach(user => {
      this.fcorgsToSubmit.push(user.fcorg);
      this.regionsToSubmit.push(user.region);
    });

    this.newGfsUser = {
      id: this.userForm.get('id').value,
      firstName: this.userForm.get('firstName').value,
      lastName: this.userForm.get('lastName').value,
      role: this.userForm.get('role').value,
      fcorgs: this.fcorgsToSubmit,
      regions: this.regionsToSubmit,
      domain: 'ND'
    };

    this.loading.track(this.gfsUserService.editUser(this.newGfsUser)).subscribe({
      error: err => {
        this.snackbar.displayError(err.error.message, DISMISS_MSG);
      },
      complete: () => {
        this.snackbar.displaySuccess('Successfully updated user', DISMISS_MSG);
        this.onCancel();
      }
    });
  }

  /**
   * Fetches user data by gmid to set userID, first name, last name, and role
   */
  loadUser(user: string): void {
    this.loading.track(this.gfsUserService.getUserByGmid(user)).subscribe(
      (data) => {
        this.editUser = data;
        this.selectedRole.viewValue = this.editUser.role;
        switch (this.editUser.role) {
          case 'Central Office Admin':
            this.selectedRole.value = '2';
            break;
          case 'Regional Admin':
            this.selectedRole.value = '3';
            break;
          case 'System Admin':
            this.selectedRole.value = '1';
            break;
          case 'User':
            this.selectedRole.value = '4';
            break;
        }
      },
      (error) => {
        this.snackbar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * Fetches the Fcorgs from the user service and sets data for the drop down
   */
  loadFcorgs(): void {
    this.loading.track(this.fcorgService.getAllFcorgs()).subscribe(
      (data) => {
        this.fcorgList = data;
      },
      (error) => {
        this.snackbar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * Fetches the Regions from the user service and sets data for the drop down
   */
  loadRegions(): void {
    this.loading.track(this.regionService.getAllRegions()).subscribe(
      (data) => {
        this.regionList = data;
      },
      (error) => {
        this.snackbar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * Fetches the user map from the user service
   */
  loadUserMap(): void {
    this.loading.track(this.gfsUserService.getUserMap(this.selectedUserId)).subscribe(
      (data) => {
        this.regionMap = data;
        this.selectedFcorgs = this.regionMap.map(fcorg => fcorg.fcorg).filter((value, index, array) => array.indexOf(value) === index);
      },
      (error) => {
        this.snackbar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * function to select fcorgs and regions from what's returned from loadUserMap
   */
  compareObjects(o1: any, o2: any): boolean {
    return o1 === o2.region + o2.fcorg || o1 === o2;
  }

  /**
   * Returns a FcorgRegionMap value based off what the user selects in the region select option
   * @param region - region value
   * @param fcorg - fcorg value
   */
  getRegionMapValue(region: string, fcorg: string): FcorgRegionMapModel {
    let mapValue = this.regionMap.find(map => map.region === region && map.fcorg === fcorg);
    if (mapValue === undefined) {
      mapValue = new FcorgRegionMapModel(null, null, region, fcorg, null);
    }
    return mapValue;
  }


  /**
   * sets fcorgRemoved flag on each selection/deselection of fcorgs
   */
  fcorgChange(event): void {
    // If an FCORG is de-selected
    if (!event.source.selected) {
      this.regionMap = this.regionMap.filter(regionMap => regionMap.fcorg !== event.source.value);
      this.dataSource.data = this.regionMap;
    }
  }

  /**
   * Cancel button navigates back to user maintenance page
   */
  onCancel(): void {
    this.router.navigate(['/admin/user-maintenance']);
  }

}
